import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart, 
  ListOrdered, 
  Map, 
  DollarSign, 
  Check, 
  AlertTriangle, 
  Info 
} from 'lucide-react';

// Color palette for inline styles
const colorPalette = {
  primary: '#922c40',   // Burgundy/wine red
  secondary: '#DC9750', // Bronze/gold
  tertiary: '#F3EAC0',  // Cream/beige
  light: '#F9F5E3',     // Lighter cream for backgrounds
  dark: '#1E2640',      // Navy/dark blue
  white: '#FFFFFF',
  black: '#333333',
  lightGray: '#F5F5F5',
  mediumGray: '#E0E0E0'
};

// Home component with landing page content
const Home = () => {
  // State for selected state in the district comparison
  const [selectedState, setSelectedState] = useState('');
  
  // States in alphabetical order
  const states = [
    { id: 'alabama', name: 'Alabama' },
    { id: 'alaska', name: 'Alaska' },
    { id: 'arizona', name: 'Arizona' },
    { id: 'arkansas', name: 'Arkansas' },
    { id: 'california', name: 'California' },
    { id: 'colorado', name: 'Colorado' },
    { id: 'connecticut', name: 'Connecticut' },
    { id: 'delaware', name: 'Delaware' },
    { id: 'florida', name: 'Florida' },
    { id: 'georgia', name: 'Georgia' },
    { id: 'hawaii', name: 'Hawaii' },
    { id: 'idaho', name: 'Idaho' },
    { id: 'illinois', name: 'Illinois' },
    { id: 'indiana', name: 'Indiana' },
    { id: 'iowa', name: 'Iowa' },
    { id: 'kansas', name: 'Kansas' },
    { id: 'kentucky', name: 'Kentucky' },
    { id: 'louisiana', name: 'Louisiana' },
    { id: 'maine', name: 'Maine' },
    { id: 'maryland', name: 'Maryland' },
    { id: 'massachusetts', name: 'Massachusetts' },
    { id: 'michigan', name: 'Michigan' },
    { id: 'minnesota', name: 'Minnesota' },
    { id: 'mississippi', name: 'Mississippi' },
    { id: 'missouri', name: 'Missouri' },
    { id: 'montana', name: 'Montana' },
    { id: 'nebraska', name: 'Nebraska' },
    { id: 'nevada', name: 'Nevada' },
    { id: 'newhampshire', name: 'New Hampshire' },
    { id: 'newjersey', name: 'New Jersey' },
    { id: 'newmexico', name: 'New Mexico' },
    { id: 'newyork', name: 'New York' },
    { id: 'northcarolina', name: 'North Carolina' },
    { id: 'northdakota', name: 'North Dakota' },
    { id: 'ohio', name: 'Ohio' },
    { id: 'oklahoma', name: 'Oklahoma' },
    { id: 'oregon', name: 'Oregon' },
    { id: 'pennsylvania', name: 'Pennsylvania' },
    { id: 'rhodeisland', name: 'Rhode Island' },
    { id: 'southcarolina', name: 'South Carolina' },
    { id: 'southdakota', name: 'South Dakota' },
    { id: 'tennessee', name: 'Tennessee' },
    { id: 'texas', name: 'Texas' },
    { id: 'utah', name: 'Utah' },
    { id: 'vermont', name: 'Vermont' },
    { id: 'virginia', name: 'Virginia' },
    { id: 'washington', name: 'Washington' },
    { id: 'westvirginia', name: 'West Virginia' },
    { id: 'wisconsin', name: 'Wisconsin' },
    { id: 'wyoming', name: 'Wyoming' }
  ];

  // State data with current and proposed district info for STATE LEGISLATURES
  const stateData = {
    alabama: {
      name: 'Alabama',
      currentDistricts: 105,
      proposedDistricts: 21,
      currentPopPerDistrict: 45_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/alabama-current.svg',
      proposedMapUrl: '/images/maps/alabama-proposed.svg',
      currentProblems: [
        'All 105 districts elect just one representative each',
        'Districts are heavily gerrymandered to favor certain parties',
        'Minority voters concentrated in single-member districts',
        'Nearly 45% of voters have no representation aligned with their views'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Minority viewpoints would gain representation in every district',
        'Gerrymandering would be effectively eliminated',
        'Over 80% of voters would help elect a representative they support'
      ]
    },
    alaska: {
      name: 'Alaska',
      currentDistricts: 40,
      proposedDistricts: 8,
      currentPopPerDistrict: 18_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/alaska-current.svg',
      proposedMapUrl: '/images/maps/alaska-proposed.svg',
      currentProblems: [
        'Single-member districts create winner-take-all results',
        'Native Alaskan communities have limited representation',
        'Urban and rural interests compete for single representation',
        'Vast geographic distances make districts unwieldy'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Both urban and rural interests would be represented in each district',
        'Native Alaskan communities would gain stronger collective voice',
        'Over 80% of voters would help elect a representative they support'
      ]
    },
    arizona: {
      name: 'Arizona',
      currentDistricts: 30,
      proposedDistricts: 10,
      currentPopPerDistrict: 240_000,
      proposedRepsPerDistrict: 9,
      currentMapUrl: '/images/maps/arizona-current.svg',
      proposedMapUrl: '/images/maps/arizona-proposed.svg',
      currentProblems: [
        'Current districts each elect 2 representatives through block voting',
        'Block voting system still produces winner-take-all results',
        'Growing Latino population underrepresented',
        'Rural and tribal areas have limited effective representation'
      ],
      proposedBenefits: [
        'Each district would elect 9 representatives proportionally',
        'True proportional representation would replace block voting',
        'Tribal communities would have enhanced representation',
        'Growing Latino population would gain appropriate representation'
      ]
    },
    arkansas: {
      name: 'Arkansas',
      currentDistricts: 100,
      proposedDistricts: 20,
      currentPopPerDistrict: 30_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/arkansas-current.svg',
      proposedMapUrl: '/images/maps/arkansas-proposed.svg',
      currentProblems: [
        'All 100 districts elect just one representative each',
        'Current districts split natural communities',
        'Minority voters are spread across districts, diluting their voting power',
        'No representation for third parties or political minorities'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Both progressive urban and conservative rural interests would gain representation',
        'Minority communities would gain stronger representation',
        'Third parties could potentially win seats with support above 15%'
      ]
    },
    california: {
      name: 'California',
      currentDistricts: 80,
      proposedDistricts: 16,
      currentPopPerDistrict: 500_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/california-current.svg',
      proposedMapUrl: '/images/maps/california-proposed.svg',
      currentProblems: [
        'All 80 districts elect just one representative each',
        'Single-member districts lead to polarization and lack of compromise',
        'Republicans severely underrepresented based on statewide vote share',
        'Over 40% of voters have no representation that aligns with their views'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Both urban and rural voices would be represented in each district',
        'Republicans would gain fair representation based on vote share',
        'Third parties could win seats with just 15% support'
      ]
    },
    colorado: {
      name: 'Colorado',
      currentDistricts: 65,
      proposedDistricts: 13,
      currentPopPerDistrict: 90_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/colorado-current.svg',
      proposedMapUrl: '/images/maps/colorado-proposed.svg',
      currentProblems: [
        'All 65 districts elect just one representative each',
        'Denver metro area divided among multiple districts',
        'Mountain communities split between different districts',
        'Growing political polarization between urban and rural areas'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Both urban and rural interests would be represented together',
        'Mountain communities would have cohesive representation',
        'Political moderates would have stronger voice in legislature'
      ]
    },
    connecticut: {
      name: 'Connecticut',
      currentDistricts: 151,
      proposedDistricts: 30,
      currentPopPerDistrict: 23_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/connecticut-current.svg',
      proposedMapUrl: '/images/maps/connecticut-proposed.svg',
      currentProblems: [
        'Connecticut has one of the largest state legislatures relative to population',
        'All 151 districts elect just one representative each',
        'Districts designed to protect incumbents',
        'Minority communities split between districts'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'More efficient legislature with better representation',
        'Both urban and rural interests would gain representation',
        'Minority communities would gain stronger collective voice'
      ]
    },
    delaware: {
      name: 'Delaware',
      currentDistricts: 41,
      proposedDistricts: 8,
      currentPopPerDistrict: 25_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/delaware-current.svg',
      proposedMapUrl: '/images/maps/delaware-proposed.svg',
      currentProblems: [
        'All 41 districts elect just one representative each',
        'Rural southern Delaware often feels underrepresented',
        'Growing minority communities have limited representation',
        'No representation for political minorities'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Both urban and rural interests would be represented together',
        'Minority communities would gain stronger representation',
        'Political minorities would gain representation in all districts'
      ]
    },
    florida: {
      name: 'Florida',
      currentDistricts: 120,
      proposedDistricts: 24,
      currentPopPerDistrict: 180_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/florida-current.svg',
      proposedMapUrl: '/images/maps/florida-proposed.svg',
      currentProblems: [
        'All 120 districts elect just one representative each',
        'Recent redistricting reduced minority representation',
        'Complex boundaries that favor the majority party',
        'Nearly half of voters have no aligned representation'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Both urban and rural interests represented in each district',
        'Minority communities would gain stronger representation',
        'Representatives would be accountable to broader constituencies'
      ]
    },
    georgia: {
      name: 'Georgia',
      currentDistricts: 180,
      proposedDistricts: 36,
      currentPopPerDistrict: 60_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/georgia-current.svg',
      proposedMapUrl: '/images/maps/georgia-proposed.svg',
      currentProblems: [
        'All 180 districts elect just one representative each',
        'Atlanta metro area divided among numerous districts',
        'Racial gerrymandering has diluted minority voting power',
        'Rural and urban interests artificially separated'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Both urban and rural interests would be represented together',
        'Black and Latino communities would gain stronger representation',
        'Most voters would help elect a representative aligned with their views'
      ]
    },
    hawaii: {
      name: 'Hawaii',
      currentDistricts: 51,
      proposedDistricts: 17,
      currentPopPerDistrict: 28_000,
      proposedRepsPerDistrict: 3,
      currentMapUrl: '/images/maps/hawaii-current.svg',
      proposedMapUrl: '/images/maps/hawaii-proposed.svg',
      currentProblems: [
        'All 51 districts elect just one representative each',
        'Islands split between districts, breaking natural communities',
        'Native Hawaiian interests often underrepresented',
        'No representation for political minorities'
      ],
      proposedBenefits: [
        'Each district would elect 3 representatives proportionally',
        'Island communities would maintain their integrity in voting',
        'Native Hawaiian interests would have stronger representation possibilities',
        'Multiple political perspectives would be represented'
      ]
    },
    idaho: {
      name: 'Idaho',
      currentDistricts: 35,
      proposedDistricts: 12,
      currentPopPerDistrict: 50_000,
      proposedRepsPerDistrict: 6,
      currentMapUrl: '/images/maps/idaho-current.svg',
      proposedMapUrl: '/images/maps/idaho-proposed.svg',
      currentProblems: [
        'Current districts each elect 2 representatives through block voting',
        'Block voting system produces winner-take-all results',
        'Boise metropolitan area has limited representation',
        'Democratic voters severely underrepresented'
      ],
      proposedBenefits: [
        'Each district would elect 6 representatives proportionally',
        'True proportional representation would replace block voting',
        'Urban Boise area would gain appropriate representation',
        'Democratic voters would gain fair representation based on vote share'
      ]
    },
    illinois: {
      name: 'Illinois',
      currentDistricts: 118,
      proposedDistricts: 24,
      currentPopPerDistrict: 110_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/illinois-current.svg',
      proposedMapUrl: '/images/maps/illinois-proposed.svg',
      currentProblems: [
        'All 118 districts elect just one representative each',
        'Complex, irregular district shapes designed for partisan advantage',
        'Chicago divided into multiple districts extending into suburbs',
        'Communities split across multiple districts'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Communities would remain whole within districts',
        'Both Chicago and downstate interests represented together',
        'Multiple viewpoints represented in each region'
      ]
    },
    indiana: {
      name: 'Indiana',
      currentDistricts: 100,
      proposedDistricts: 20,
      currentPopPerDistrict: 67_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/indiana-current.svg',
      proposedMapUrl: '/images/maps/indiana-proposed.svg',
      currentProblems: [
        'All 100 districts elect just one representative each',
        'Indianapolis metro area divided among multiple districts',
        'Districts designed to minimize Democratic voting power',
        'Minority communities split to reduce their electoral influence'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Urban and rural interests would both have representation',
        'Minority communities would gain stronger representation',
        'Democratic voters would gain fair representation based on vote share'
      ]
    },
    iowa: {
      name: 'Iowa',
      currentDistricts: 100,
      proposedDistricts: 20,
      currentPopPerDistrict: 31_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/iowa-current.svg',
      proposedMapUrl: '/images/maps/iowa-proposed.svg',
      currentProblems: [
        'All 100 districts elect just one representative each',
        'Though better than most states, communities still divided',
        'Urban areas spread across multiple districts',
        'No representation for third parties or political minorities'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Both urban and rural interests would gain representation',
        'Growing minority communities would have paths to representation',
        'Third parties could potentially win seats with over 15% support'
      ]
    },
    kansas: {
      name: 'Kansas',
      currentDistricts: 125,
      proposedDistricts: 25,
      currentPopPerDistrict: 23_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/kansas-current.svg',
      proposedMapUrl: '/images/maps/kansas-proposed.svg',
      currentProblems: [
        'All 125 districts elect just one representative each',
        'Kansas City metropolitan area divided to dilute Democratic votes',
        'Eastern urban and western rural areas have competing interests',
        'Growing Latino communities underrepresented'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Both urban and rural interests would gain representation',
        'Growing Latino communities would have stronger voice',
        'Moderate Kansans would regain representation'
      ]
    },
    kentucky: {
      name: 'Kentucky',
      currentDistricts: 100,
      proposedDistricts: 20,
      currentPopPerDistrict: 45_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/kentucky-current.svg',
      proposedMapUrl: '/images/maps/kentucky-proposed.svg',
      currentProblems: [
        'All 100 districts elect just one representative each',
        'Louisville and Lexington split to dilute urban votes',
        'Appalachian communities divided among districts',
        'African American communities fragmented to reduce voting power'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Urban centers would gain appropriate representation',
        'Appalachian communities would have unified representation',
        'Both progressive and conservative views represented'
      ]
    },
    louisiana: {
      name: 'Louisiana',
      currentDistricts: 105,
      proposedDistricts: 21,
      currentPopPerDistrict: 45_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/louisiana-current.svg',
      proposedMapUrl: '/images/maps/louisiana-proposed.svg',
      currentProblems: [
        'All 105 districts elect just one representative each',
        'African American communities dispersed across districts',
        'New Orleans area divided to minimize urban voting power',
        'Only one-third of districts typically elect minority representatives'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'African American voters would gain appropriate representation',
        'Urban and rural interests both represented',
        'Every major political perspective would gain representation'
      ]
    },
    maine: {
      name: 'Maine',
      currentDistricts: 151,
      proposedDistricts: 30,
      currentPopPerDistrict: 9_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/maine-current.svg',
      proposedMapUrl: '/images/maps/maine-proposed.svg',
      currentProblems: [
        'Maine has one of the largest state legislatures relative to population',
        'All 151 districts elect just one representative each',
        'Urban coastal and rural inland interests divided',
        'Growing political polarization between districts'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Both coastal and inland interests would have representation',
        'Third parties could potentially win representation',
        'More efficient legislature with stronger representation'
      ]
    },
    maryland: {
      name: 'Maryland',
      currentDistricts: 47,
      proposedDistricts: 16,
      currentPopPerDistrict: 130_000,
      proposedRepsPerDistrict: 9,
      currentMapUrl: '/images/maps/maryland-current.svg',
      proposedMapUrl: '/images/maps/maryland-proposed.svg',
      currentProblems: [
        'Current districts each elect 3 representatives through block voting',
        'Block voting system still produces winner-take-all results',
        'Complex, irregular districts designed for partisan advantage',
        'Baltimore city divided among multiple districts'
      ],
      proposedBenefits: [
        'Each district would elect 9 representatives through true proportional representation',
        'Both urban and rural interests would be represented together',
        'Communities would remain intact within districts',
        'Republicans would gain fair representation based on vote share'
      ]
    },
    massachusetts: {
      name: 'Massachusetts',
      currentDistricts: 160,
      proposedDistricts: 32,
      currentPopPerDistrict: 43_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/massachusetts-current.svg',
      proposedMapUrl: '/images/maps/massachusetts-proposed.svg',
      currentProblems: [
        'All 160 districts elect just one representative each',
        'Boston metro area divided among multiple districts',
        'Republican voters severely underrepresented based on statewide vote share',
        'Growing minority communities underrepresented'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Republicans would gain fair representation based on vote share',
        'Minority communities would have stronger representation',
        'Third parties could potentially win seats with sufficient support'
      ]
    },
    michigan: {
      name: 'Michigan',
      currentDistricts: 110,
      proposedDistricts: 22,
      currentPopPerDistrict: 90_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/michigan-current.svg',
      proposedMapUrl: '/images/maps/michigan-proposed.svg',
      currentProblems: [
        'All 110 districts elect just one representative each',
        'Detroit metro area divided among multiple districts',
        'Upper Peninsula combined with parts of Lower Michigan despite different interests',
        'African American communities fragmented to reduce voting power'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Both urban and rural interests would be represented together',
        'Upper Peninsula would have dedicated representation',
        'African American communities would gain appropriate representation'
      ]
    },
    minnesota: {
      name: 'Minnesota',
      currentDistricts: 134,
      proposedDistricts: 27,
      currentPopPerDistrict: 42_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/minnesota-current.svg',
      proposedMapUrl: '/images/maps/minnesota-proposed.svg',
      currentProblems: [
        'All 134 districts elect just one representative each',
        'Twin Cities metro area divided among multiple districts',
        'Rural northern interests often underrepresented',
        'Native American reservations split between districts'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Twin Cities would have appropriate representation',
        'Rural northern Minnesota would gain stronger voice',
        'Native American communities would have paths to representation'
      ]
    },
    mississippi: {
      name: 'Mississippi',
      currentDistricts: 122,
      proposedDistricts: 24,
      currentPopPerDistrict: 24_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/mississippi-current.svg',
      proposedMapUrl: '/images/maps/mississippi-proposed.svg',
      currentProblems: [
        'All 122 districts elect just one representative each',
        'African American communities dispersed across districts',
        'Only one-third of districts typically elect a minority representative',
        'Current districts have faced multiple court challenges over racial bias'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'African American voters would gain appropriate representation',
        'Both Delta and Gulf Coast regions would have representation',
        'Every major political perspective would gain representation'
      ]
    },
    missouri: {
      name: 'Missouri',
      currentDistricts: 163,
      proposedDistricts: 33,
      currentPopPerDistrict: 38_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/missouri-current.svg',
      proposedMapUrl: '/images/maps/missouri-proposed.svg',
      currentProblems: [
        'All 163 districts elect just one representative each',
        'St. Louis and Kansas City divided to dilute urban votes',
        'Rural communities artificially separated from natural regions',
        'African American communities fragmented to reduce voting power'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Urban centers would gain appropriate representation',
        'Rural communities would maintain strong representation',
        'Political moderates would regain influence'
      ]
    },
    montana: {
      name: 'Montana',
      currentDistricts: 100,
      proposedDistricts: 20,
      currentPopPerDistrict: 11_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/montana-current.svg',
      proposedMapUrl: '/images/maps/montana-proposed.svg',
      currentProblems: [
        'All 100 districts elect just one representative each',
        'Eastern and western Montana have very different interests',
        'Native American reservations split between districts',
        'Growing urban-rural divide not adequately addressed'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Both eastern and western interests would be represented',
        'Native American communities would have stronger voice',
        'Urban and rural concerns both addressed'
      ]
    },
    nebraska: {
      name: 'Nebraska',
      currentDistricts: 49,
      proposedDistricts: 10,
      currentPopPerDistrict: 40_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/nebraska-current.svg',
      proposedMapUrl: '/images/maps/nebraska-proposed.svg',
      currentProblems: [
        'Nebraska has a unicameral legislature with only 49 single-member districts',
        'Omaha metro area split to dilute urban votes',
        'Western rural interests often overshadowed',
        'Growing Latino communities underrepresented'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Urban Omaha and Lincoln would have appropriate representation',
        'Rural western Nebraska would maintain strong voice',
        'Growing minority communities would have paths to representation'
      ]
    },
    nevada: {
      name: 'Nevada',
      currentDistricts: 42,
      proposedDistricts: 9,
      currentPopPerDistrict: 73_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/nevada-current.svg',
      proposedMapUrl: '/images/maps/nevada-proposed.svg',
      currentProblems: [
        'All 42 districts elect just one representative each',
        'Las Vegas metro area divided among multiple districts',
        'Rural communities have limited effective representation',
        'Latino communities split to minimize their electoral impact'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Las Vegas would have appropriate representation',
        'Rural Nevada would maintain strong voice',
        'Latino communities would gain stronger representation'
      ]
    },
    newhampshire: {
      name: 'New Hampshire',
      currentDistricts: 400,
      proposedDistricts: 40,
      currentPopPerDistrict: 3_400,
      proposedRepsPerDistrict: 10,
      currentMapUrl: '/images/maps/newhampshire-current.svg',
      proposedMapUrl: '/images/maps/newhampshire-proposed.svg',
      currentProblems: [
        'New Hampshire has the largest state legislature in the US with 400 members',
        'Districts vary widely in size and number of representatives',
        'Some districts are "floterial" districts that overlap with others',
        'Complex system dilutes representation in many areas'
      ],
      proposedBenefits: [
        'Each district would elect 10 representatives proportionally',
        'More efficient and effective legislature structure',
        'Both urban and rural interests would have fair representation',
        'Third parties could potentially win representation'
      ]
    },
    newjersey: {
      name: 'New Jersey',
      currentDistricts: 40,
      proposedDistricts: 8,
      currentPopPerDistrict: 225_000,
      proposedRepsPerDistrict: 10,
      currentMapUrl: '/images/maps/newjersey-current.svg',
      proposedMapUrl: '/images/maps/newjersey-proposed.svg',
      currentProblems: [
        'Current districts each elect 2 representatives through block voting',
        'Block voting system produces winner-take-all results',
        'Urban communities divided among multiple districts',
        'Growing minority communities underrepresented'
      ],
      proposedBenefits: [
        'Each district would elect 10 representatives proportionally',
        'True proportional representation would replace block voting',
        'Urban centers would gain appropriate representation',
        'Minority communities would have stronger voice'
      ]
    },
    newmexico: {
      name: 'New Mexico',
      currentDistricts: 70,
      proposedDistricts: 14,
      currentPopPerDistrict: 30_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/newmexico-current.svg',
      proposedMapUrl: '/images/maps/newmexico-proposed.svg',
      currentProblems: [
        'All 70 districts elect just one representative each',
        'Native American reservations split between districts',
        'Latino communities divided to dilute voting power',
        'Urban Albuquerque divided among districts'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Native American communities would gain stronger voice',
        'Latino communities would have appropriate representation',
        'Both urban and rural interests represented'
      ]
    },
    newyork: {
      name: 'New York',
      currentDistricts: 150,
      proposedDistricts: 30,
      currentPopPerDistrict: 135_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/newyork-current.svg',
      proposedMapUrl: '/images/maps/newyork-proposed.svg',
      currentProblems: [
        'All 150 districts elect just one representative each',
        'Complex, winding district boundaries that split communities',
        'Urban centers divided to maintain partisan advantage',
        'Many voters effectively disenfranchised in "safe" districts'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Geographic communities would remain intact',
        'Multiple representatives would serve overlapping areas',
        'Greater ideological diversity in the legislature'
      ]
    },
    northcarolina: {
      name: 'North Carolina',
      currentDistricts: 120,
      proposedDistricts: 24,
      currentPopPerDistrict: 85_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/northcarolina-current.svg',
      proposedMapUrl: '/images/maps/northcarolina-proposed.svg',
      currentProblems: [
        'All 120 districts elect just one representative each',
        'History of extreme racial and partisan gerrymandering',
        'Multiple court challenges to district maps',
        'African American communities fragmented to reduce voting power'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Urban and rural interests both properly represented',
        'African American communities would gain appropriate representation',
        'Every major political perspective would be represented'
      ]
    },
    northdakota: {
      name: 'North Dakota',
      currentDistricts: 47,
      proposedDistricts: 9,
      currentPopPerDistrict: 16_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/northdakota-current.svg',
      proposedMapUrl: '/images/maps/northdakota-proposed.svg',
      currentProblems: [
        'Current districts each elect 2 representatives through block voting',
        'Block voting system produces winner-take-all results',
        'Native American reservations have limited representation',
        'Urban centers like Fargo have limited influence'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'True proportional representation would replace block voting',
        'Native American communities would gain stronger voice',
        'Urban centers would gain appropriate representation'
      ]
    },
    ohio: {
      name: 'Ohio',
      currentDistricts: 99,
      proposedDistricts: 20,
      currentPopPerDistrict: 120_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/ohio-current.svg',
      proposedMapUrl: '/images/maps/ohio-proposed.svg',
      currentProblems: [
        'All 99 districts elect just one representative each',
        'Major cities divided among multiple districts',
        'Extreme partisan gerrymandering despite reform attempts',
        'African American communities fragmented to reduce voting power'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Major cities would have appropriate representation',
        'Minority communities would gain stronger voice',
        'Both urban and rural perspectives represented in each district'
      ]
    },
    oklahoma: {
      name: 'Oklahoma',
      currentDistricts: 101,
      proposedDistricts: 20,
      currentPopPerDistrict: 40_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/oklahoma-current.svg',
      proposedMapUrl: '/images/maps/oklahoma-proposed.svg',
      currentProblems: [
        'All 101 districts elect just one representative each',
        'Oklahoma City and Tulsa divided among multiple districts',
        'Native American communities split to minimize their electoral impact',
        'Democratic voters severely underrepresented'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Urban centers would gain appropriate representation',
        'Native American communities would have stronger voice',
        'Democratic voters would gain fair representation based on vote share'
      ]
    },
    oregon: {
      name: 'Oregon',
      currentDistricts: 60,
      proposedDistricts: 12,
      currentPopPerDistrict: 70_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/oregon-current.svg',
      proposedMapUrl: '/images/maps/oregon-proposed.svg',
      currentProblems: [
        'All 60 districts elect just one representative each',
        'Portland metro area divided among multiple districts',
        'Stark urban-rural divide not adequately addressed',
        'Coastal and inland interests artificially separated'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Urban Portland area would have appropriate representation',
        'Rural eastern Oregon would maintain strong voice',
        'Political moderates would regain influence'
      ]
    },
    pennsylvania: {
      name: 'Pennsylvania',
      currentDistricts: 203,
      proposedDistricts: 41,
      currentPopPerDistrict: 63_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/pennsylvania-current.svg',
      proposedMapUrl: '/images/maps/pennsylvania-proposed.svg',
      currentProblems: [
        'All 203 districts elect just one representative each',
        'History of extreme gerrymandering and court challenges',
        'Oddly shaped districts that divide communities',
        'Strong geographic polarization between urban and rural areas'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Urban, suburban, and rural voices in each district',
        'More localized representation with multiple representatives',
        'Less political polarization as diverse views are represented'
      ]
    },
    rhodeisland: {
      name: 'Rhode Island',
      currentDistricts: 75,
      proposedDistricts: 15,
      currentPopPerDistrict: 14_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/rhodeisland-current.svg',
      proposedMapUrl: '/images/maps/rhodeisland-proposed.svg',
      currentProblems: [
        'All 75 districts elect just one representative each',
        'Providence metro area divided between districts',
        'Political minorities have no representation',
        'Republican voters severely underrepresented'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Providence would have appropriate representation',
        'Minority communities would have stronger voice',
        'Republicans would gain fair representation based on vote share'
      ]
    },
    southcarolina: {
      name: 'South Carolina',
      currentDistricts: 124,
      proposedDistricts: 25,
      currentPopPerDistrict: 42_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/southcarolina-current.svg',
      proposedMapUrl: '/images/maps/southcarolina-proposed.svg',
      currentProblems: [
        'All 124 districts elect just one representative each',
        'Only one-third of districts typically elect a minority representative',
        'African American communities dispersed across districts',
        'Current districts have faced multiple court challenges over racial bias'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'African American voters would gain appropriate representation',
        'Both coastal and inland interests represented',
        'Democratic voters would gain fair representation based on vote share'
      ]
    },
    southdakota: {
      name: 'South Dakota',
      currentDistricts: 35,
      proposedDistricts: 7,
      currentPopPerDistrict: 25_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/southdakota-current.svg',
      proposedMapUrl: '/images/maps/southdakota-proposed.svg',
      currentProblems: [
        'Current districts each elect 2 representatives through block voting',
        'Block voting system produces winner-take-all results',
        'Native American reservations have limited representation',
        'Urban centers like Sioux Falls have limited influence'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'True proportional representation would replace block voting',
        'Native American communities would have stronger voice',
        'Urban centers would gain appropriate representation'
      ]
    },
    tennessee: {
      name: 'Tennessee',
      currentDistricts: 99,
      proposedDistricts: 20,
      currentPopPerDistrict: 67_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/tennessee-current.svg',
      proposedMapUrl: '/images/maps/tennessee-proposed.svg',
      currentProblems: [
        'All 99 districts elect just one representative each',
        'Major cities divided to minimize urban voting power',
        'Nashville split among multiple districts',
        'African American communities fragmented to reduce voting power'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Major cities would have appropriate representation',
        'African American communities would gain stronger voice',
        'Democratic voters would gain fair representation based on vote share'
      ]
    },
    texas: {
      name: 'Texas',
      currentDistricts: 150,
      proposedDistricts: 30,
      currentPopPerDistrict: 195_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/texas-current.svg',
      proposedMapUrl: '/images/maps/texas-proposed.svg',
      currentProblems: [
        'All 150 districts elect just one representative each',
        'Heavily gerrymandered to favor the majority party',
        'Urban areas are "cracked" to dilute their voting power',
        'Latino and Black communities often split between districts'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Communities of interest would be represented together',
        'Political minorities would gain representation',
        'Latino and Black communities would gain stronger voice'
      ]
    },
    utah: {
      name: 'Utah',
      currentDistricts: 75,
      proposedDistricts: 15,
      currentPopPerDistrict: 42_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/utah-current.svg',
      proposedMapUrl: '/images/maps/utah-proposed.svg',
      currentProblems: [
        'All 75 districts elect just one representative each',
        'Salt Lake City divided among multiple districts to dilute urban votes',
        'Democratic voters severely underrepresented',
        'Growing minority communities underrepresented'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Salt Lake City would have appropriate representation',
        'Democratic voters would gain fair representation based on vote share',
        'Growing minority communities would have stronger voice'
      ]
    },
    vermont: {
      name: 'Vermont',
      currentDistricts: 150,
      proposedDistricts: 30,
      currentPopPerDistrict: 4_200,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/vermont-current.svg',
      proposedMapUrl: '/images/maps/vermont-proposed.svg',
      currentProblems: [
        'Vermont has a very large legislature relative to population',
        'Districts vary widely in size with some electing multiple representatives',
        'Uneven representation across districts',
        'Republican voters underrepresented in many areas'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'More efficient legislature structure',
        'Consistent representation across all districts',
        'Republican voters would gain fair representation based on vote share'
      ]
    },
    virginia: {
      name: 'Virginia',
      currentDistricts: 100,
      proposedDistricts: 20,
      currentPopPerDistrict: 86_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/virginia-current.svg',
      proposedMapUrl: '/images/maps/virginia-proposed.svg',
      currentProblems: [
        'All 100 districts elect just one representative each',
        'Northern Virginia suburbs divided among multiple districts',
        'African American communities in Hampton Roads split to reduce voting power',
        'Appalachian region has limited voice'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Northern Virginia would have appropriate representation',
        'African American communities would gain stronger voice',
        'Regional differences would be represented while ensuring diversity'
      ]
    },
    washington: {
      name: 'Washington',
      currentDistricts: 49,
      proposedDistricts: 10,
      currentPopPerDistrict: 153_000,
      proposedRepsPerDistrict: 10,
      currentMapUrl: '/images/maps/washington-current.svg',
      proposedMapUrl: '/images/maps/washington-proposed.svg',
      currentProblems: [
        'Current districts each elect 2 representatives through block voting',
        'Block voting system produces winner-take-all results',
        'Seattle metro area divided among multiple districts',
        'Growing Latino communities in eastern Washington underrepresented'
      ],
      proposedBenefits: [
        'Each district would elect 10 representatives through true proportional representation',
        'Seattle would have appropriate representation',
        'Eastern Washington would maintain strong voice',
        'Latino communities would gain representation'
      ]
    },
    westvirginia: {
      name: 'West Virginia',
      currentDistricts: 100,
      proposedDistricts: 20,
      currentPopPerDistrict: 18_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/westvirginia-current.svg',
      proposedMapUrl: '/images/maps/westvirginia-proposed.svg',
      currentProblems: [
        'All 100 districts elect just one representative each',
        'Economic and geographic regions split between districts',
        'Urban centers have limited voice',
        'Democratic voters severely underrepresented'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Natural regions would have cohesive representation',
        'Urban centers would gain appropriate voice',
        'Democratic voters would gain fair representation based on vote share'
      ]
    },
    wisconsin: {
      name: 'Wisconsin',
      currentDistricts: 99,
      proposedDistricts: 20,
      currentPopPerDistrict: 60_000,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/wisconsin-current.svg',
      proposedMapUrl: '/images/maps/wisconsin-proposed.svg',
      currentProblems: [
        'All 99 districts elect just one representative each',
        'Extreme partisan gerrymandering despite court challenges',
        'Milwaukee area divided to dilute urban votes',
        'African American communities fragmented to reduce voting power'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Milwaukee would have appropriate representation',
        'Democratic voters would gain fair representation based on vote share',
        'Both urban and rural interests represented in each district'
      ]
    },
    wyoming: {
      name: 'Wyoming',
      currentDistricts: 60,
      proposedDistricts: 12,
      currentPopPerDistrict: 9_800,
      proposedRepsPerDistrict: 5,
      currentMapUrl: '/images/maps/wyoming-current.svg',
      proposedMapUrl: '/images/maps/wyoming-proposed.svg',
      currentProblems: [
        'All 60 districts elect just one representative each',
        'Districts designed to favor majority party',
        'Native American reservations have limited representation',
        'Democratic voters severely underrepresented'
      ],
      proposedBenefits: [
        'Each district would elect 5 representatives proportionally',
        'Urban centers would gain appropriate representation',
        'Native American communities would have stronger voice',
        'Democratic voters would gain fair representation based on vote share'
      ]
    }
  };

  // Handle state selection change
  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
  };

  // Determine if state data exists
  const stateInfo = selectedState ? stateData[selectedState] : null;

  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>Transform American Democracy</h1>
            <p>Our democracy is broken—we're fighting to fix it! Our mission is to end the corrupt two-party system, get big money out of politics, and hold our representatives accountable to the American people—because Voters Deserve More.</p>
            <div className="hero-buttons">
              <Link to="/get-involved" className="cta-button">Get Involved</Link>
              <Link to="/simulator" className="secondary-button">Try Our Simulator</Link>
            </div>
          </div>
          <div className="hero-image">
            {/* Placeholder for hero image */}
            <div className="placeholder-image"></div>
          </div>
        </div>
      </section>

      {/* Why Reform Section */}
      <section className="why-reform">
        <div className="container">
          <div className="two-column">
            <div className="content">
              <h2>Why We Need Reform</h2>
              <p>Everyday, Americans are failed by our broken two-party system. Each election cycle forces voters to choose between the "lesser of two evils" rather than for candidates who represent our true beliefs. Third parties never see office, written off as "spoilers" before they can get off the ground. Dark money corrupts government, making representatives loyal to their donors and lobbyists instead of their constituents. No matter our politics, we all suffer.</p>
              <ul className="stats-list">
                <li>
                  <a href="https://news.gallup.com/poll/512135/support-third-political-party.aspx" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                    <span className="stat">63%</span> of Americans support a viable third party
                  </a>
                </li>
                <li>
                  <a href="https://protectdemocracy.org/work/how-open-americans-electoral-system-reform/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                    <span className="stat">62%</span> of Americans support proportional representation after a basic explanation
                  </a>
                </li>
                <li>
                  <a href="https://www.pewresearch.org/politics/2022/08/09/as-partisan-hostility-grows-signs-of-frustration-with-the-two-party-system/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                    <span className="stat">43%</span> of Americans say the Democrats govern "honestly and ethically"
                  </a>
                </li>
                <li>
                  <a href="https://www.pewresearch.org/politics/2022/08/09/as-partisan-hostility-grows-signs-of-frustration-with-the-two-party-system/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                    <span className="stat">37%</span> of Americans say the Republicans govern "honestly and ethically"
                  </a>
                </li>
              </ul>
              <p>It's time for a new approach that gives voters more choices and ensures every vote counts.</p>
              <Link to="/voting-systems" className="text-button">Learn more about voting systems <span>→</span></Link>
            </div>
            <div className="image">
              {/* Placeholder for reform image */}
              <div className="placeholder-image"></div>
            </div>
          </div>
        </div>
      </section>

      {/* State District Comparison - REPLACING THE PREVIOUS SIMULATOR */}
      <section className="district-comparison">
        <div className="container">
          <div className="district-comparison-container" style={{ 
            backgroundColor: colorPalette.light,
            borderRadius: '12px', 
            padding: '30px',
            marginBottom: '40px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ 
              fontSize: '1.8rem',
              color: colorPalette.primary,
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              See How Multi-Member Districts Would Transform Your State Legislature
            </h2>
            
            <div style={{
              backgroundColor: colorPalette.tertiary,
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '25px'
            }}>
              <p style={{ 
                margin: '0',
                fontSize: '1.1rem',
                lineHeight: '1.5'
              }}>
                Multi-member districts are larger geographic areas that elect <strong>multiple representatives at once</strong>. 
                They enable proportional representation, eliminate gerrymandering, and ensure more voters have representatives who share their values.
              </p>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              marginBottom: '30px'
            }}>
              <div style={{
                width: '100%',
                maxWidth: '400px'
              }}>
                <label 
                  htmlFor="state-select" 
                  style={{ 
                    display: 'block', 
                    marginBottom: '10px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: colorPalette.dark
                  }}
                >
                  Select your state:
                </label>
                <select 
                  id="state-select"
                  value={selectedState}
                  onChange={handleStateChange}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    fontSize: '1.1rem',
                    borderRadius: '8px',
                    border: `2px solid ${colorPalette.secondary}`,
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23${colorPalette.secondary.replace('#', '')}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 15px center',
                    backgroundSize: '15px'
                  }}
                >
                  <option value="">-- Select a state --</option>
                  {states.map(state => (
                    <option key={state.id} value={state.id}>{state.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {!selectedState && (
              <div style={{
                textAlign: 'center',
                padding: '30px',
                backgroundColor: 'white',
                borderRadius: '8px',
                borderLeft: `4px solid ${colorPalette.secondary}`,
                marginBottom: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '10px'
                }}>
                  <Info size={24} color={colorPalette.secondary} style={{ marginRight: '10px' }} />
                  <span style={{ fontWeight: '600', fontSize: '1.1rem', color: colorPalette.dark }}>
                    Select a state to see how multi-member districts would work there
                  </span>
                </div>
                <p style={{ margin: '0', color: '#666' }}>
                  We'll show you how your current state legislature districts compare to a multi-member district system
                </p>
              </div>
            )}
            
            {stateInfo && (
              <div className="state-comparison" style={{ 
                backgroundColor: 'white',
                borderRadius: '10px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <div style={{
                  backgroundColor: colorPalette.dark,
                  color: 'white',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <h3 style={{ 
                    margin: '0',
                    fontSize: '1.6rem',
                    fontWeight: '700'
                  }}>
                    {stateInfo.name} State Legislative Districts
                  </h3>
                </div>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '20px',
                  padding: '25px'
                }}>
                  {/* Current Districts Column */}
                  <div className="column" style={{ 
                    display: 'flex', 
                    flexDirection: 'column'
                  }}>
                  <div style={{
                    backgroundColor: colorPalette.light,
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    flex: '0 0 auto'
                  }}>
                    <h4 style={{ 
                      margin: '0 0 15px 0',
                      color: colorPalette.dark,
                      fontSize: '1.2rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <AlertTriangle size={20} color={colorPalette.primary} style={{ marginRight: '8px' }} />
                      Current System: {stateInfo.currentDistricts} Single-Member Districts
                    </h4>
                    <div style={{
                      color: '#666',
                      fontSize: '0.9rem',
                      marginBottom: '15px'
                    }}>
                      <div>Each district elects just 1 representative</div>
                      <div>Average population per district: {stateInfo.currentPopPerDistrict.toLocaleString()}</div>
                    </div>
                  </div>
                  
                  {/* Placeholder for current district map */}
                  <div style={{
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    height: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}>
                    <div style={{ textAlign: 'center', color: '#666' }}>
                      <Map size={40} />
                      <div>Current {stateInfo.currentDistricts} districts map</div>
                      <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '5px' }}>
                        (SVG map would be displayed here)
                      </div>
                    </div>
                  </div>
                  
                  <div style={{
                    backgroundColor: `${colorPalette.primary}10`,
                    borderLeft: `4px solid ${colorPalette.primary}`,
                    padding: '15px',
                    borderRadius: '0 8px 8px 0',
                    marginBottom: '20px'
                  }}>
                    <h5 style={{
                      margin: '0 0 10px 0',
                      color: colorPalette.primary,
                      fontSize: '1rem'
                    }}>Problems with Current Districts:</h5>
                    <ul style={{
                      margin: '0',
                      paddingLeft: '20px',
                      fontSize: '0.9rem'
                    }}>
                      {stateInfo.currentProblems.map((problem, index) => (
                        <li key={index} style={{ marginBottom: '8px' }}>{problem}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                {/* Proposed Districts Column */}
                <div className="column" style={{ 
                  display: 'flex', 
                  flexDirection: 'column'
                }}>
                  <div style={{
                    backgroundColor: colorPalette.tertiary,
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    flex: '0 0 auto'
                  }}>
                    <h4 style={{ 
                      margin: '0 0 15px 0',
                      color: colorPalette.dark,
                      fontSize: '1.2rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Check size={20} color={colorPalette.secondary} style={{ marginRight: '8px' }} />
                      Proposed: {stateInfo.proposedDistricts} Multi-Member Districts
                    </h4>
                    <div style={{
                      color: '#666',
                      fontSize: '0.9rem',
                      marginBottom: '15px'
                    }}>
                      <div>Each district elects {stateInfo.proposedRepsPerDistrict} representatives</div>
                      <div>Total representatives: {stateInfo.proposedDistricts * stateInfo.proposedRepsPerDistrict} (same as current)</div>
                    </div>
                  </div>
                  
                  {/* Placeholder for proposed district map */}
                  <div style={{
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    height: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}>
                    <div style={{ textAlign: 'center', color: '#666' }}>
                      <Map size={40} />
                      <div>Proposed {stateInfo.proposedDistricts} districts map</div>
                      <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '5px' }}>
                        (SVG map would be displayed here)
                      </div>
                    </div>
                  </div>
                  
                  <div style={{
                    backgroundColor: `${colorPalette.secondary}10`,
                    borderLeft: `4px solid ${colorPalette.secondary}`,
                    padding: '15px',
                    borderRadius: '0 8px 8px 0',
                    marginBottom: '20px'
                  }}>
                    <h5 style={{
                      margin: '0 0 10px 0',
                      color: colorPalette.secondary,
                      fontSize: '1rem'
                    }}>Benefits of Multi-Member Districts:</h5>
                    <ul style={{
                      margin: '0',
                      paddingLeft: '20px',
                      fontSize: '0.9rem'
                    }}>
                      {stateInfo.proposedBenefits.map((benefit, index) => (
                        <li key={index} style={{ marginBottom: '8px' }}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div style={{
                padding: '20px',
                backgroundColor: colorPalette.light,
                borderTop: `1px solid ${colorPalette.mediumGray}`
              }}>
                <h4 style={{
                  margin: '0 0 15px 0',
                  color: colorPalette.dark,
                  fontSize: '1.2rem',
                  textAlign: 'center'
                }}>How Would This Affect Your Vote?</h4>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '20px',
                }}>
                  <div style={{
                    backgroundColor: 'white',
                    padding: '15px',
                    borderRadius: '8px',
                    border: `1px solid ${colorPalette.primary}`,
                    textAlign: 'center'
                  }}>
                    <h5 style={{ 
                      color: colorPalette.primary, 
                      margin: '0 0 10px 0',
                      fontSize: '1rem'
                    }}>
                      Current System
                    </h5>
                    <p style={{ margin: '0', fontSize: '0.9rem' }}>
                      You vote for 1 representative in your small district.
                      If your preferred candidate loses, your vote doesn't help elect anyone.
                    </p>
                  </div>
                  
                  <div style={{
                    backgroundColor: 'white',
                    padding: '15px',
                    borderRadius: '8px',
                    border: `1px solid ${colorPalette.secondary}`,
                    textAlign: 'center'
                  }}>
                    <h5 style={{ 
                      color: colorPalette.secondary, 
                      margin: '0 0 10px 0',
                      fontSize: '1rem'
                    }}>
                      Multi-Member Districts
                    </h5>
                    <p style={{ margin: '0', fontSize: '0.9rem' }}>
                      You help elect {stateInfo.proposedRepsPerDistrict} representatives in a larger district.
                      Your vote counts toward electing someone who shares your values.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div style={{
            marginTop: '30px',
            textAlign: 'center'
          }}>
            <a 
              href="/simulator"
              style={{
                display: 'inline-block',
                backgroundColor: colorPalette.primary,
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease'
              }}
            >
              Try Our Interactive Voting Simulator
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* Features Section */}
    <section className="features">
      <div className="container">
        <div className="section-header">
          <h2>Reviving Democracy</h2>
          <p>Our comprehensive plan for Proportional Electoral Reform (PER) aims to radically transform our electoral system into one that serves all Americans, not the two-party establishment and its corporate donors. Our vision for a new American democracy includes:</p>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <BarChart className="icon" color="white" />
            </div>
            <h3>Proportional Representation</h3>
            <p>Democracy means more than choosing the lesser evil--under proportional representation, voters of all beliefs get their fair share. If a party wins 30% of the vote, they get 30% of the seats. It's that simple.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <ListOrdered className="icon" color="white" />
            </div>
            <h3>Popular Vote for Presidents</h3>
            <p>Instead of a broken institution, the Electoral College, choosing the President, we advocate that the people directly choose the President via the passage of the National Popular Vote Interstate Compact.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <Map className="icon" color="white" />
            </div>
            <h3>End Gerrymandering</h3>
            <p>Multi-member districts with proportional representation eliminate the ability to rig district lines for partisan advantage. No more allowing greedy politicians to rig maps in their own favor just to retain power.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <DollarSign className="icon" color="white" />
            </div>
            <h3>No More Dark Money</h3>
            <p>We want to ban politicians from accepting funds from foreign governments, large corporations, or undisclosed sources. Instead, we advocate to establish a public financing system that funds candidates equally and makes them accountable to voters rather than wealthy lobbyists.</p>
          </div>
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="cta">
      <div className="container">
        <h2>Join the Movement</h2>
        <p>Together, we can build a democracy that represents all Americans. Sign up to get involved, receive updates, and help bring proportional representation to your community.</p>
        <form className="signup-form">
          <input type="email" placeholder="Your email address" required />
          <button type="submit" className="cta-button">Join Us</button>
        </form>
      </div>
    </section>
  </>
);
};

export default Home;